/*!
 * random.js — the random() cue: a dice roll as a signal source
 * © 2025 Rob Canning — GPLv3
 *
 * Rolls a number when it fires and publishes it, so anything bindable can be
 * driven by chance rather than by a fader or the playhead:
 *
 *   random(uid:dice, min:1, max:8, int:1, trig:"adc.onset>0.5")
 *   image(path:plates, index: follow(dice.value))
 *
 * — an attack on the microphone deals a new plate from the folder.
 *
 * WHY NOT rand(). rand(min,max) already exists as a VALUE function inside
 * patterns and value lists (`values:Pseq(1, rand(0.5, 2), inf)`), and it is
 * used in live scores. Unlike adc there is no lookahead that separates the two
 * — both are literally "rand(" — so a cue of that name would claim the word
 * everywhere and break them. This is the standalone object; rand() stays the
 * inline function.
 *
 * WHY IT IS A CUE AND NOT A FUNCTION ON image(). Because a source with an
 * outlet composes: the same roll can drive a picture, a synth's pitch, a
 * scale and a nav all at once, and it shows up in the patch view as an object
 * with a cord leaving it. image(index: rand(1,8)) could only ever serve image.
 *
 * Outlets:
 *   value   the roll, in the min..max range you asked for
 *   norm    the same roll as 0-1, for binding without restating the range
 */

import { publish } from "../control/paramBinding.js";

/** Pull the numeric parameters out of a cue's arg list. */
function readParams(ast) {
  const p = {};
  for (const a of (ast?.args || [])) if (a?.type) p[a.type] = a.value;
  const num = (v, dflt) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  };
  let min = num(p.min, 0);
  let max = num(p.max, 1);
  // A reversed range is a typo, not an instruction — read it the way it was
  // obviously meant rather than returning NaN or always the same number.
  if (min > max) [min, max] = [max, min];
  return {
    uid: p.uid ? String(p.uid) : "random",
    min, max,
    // int:1 rounds to whole numbers. Folder indices, MIDI notes and bar
    // numbers all want this, and it is the common case for min/max integers.
    asInt: p.int === 1 || p.int === "1" || p.int === true,
  };
}

/**
 * Roll once and publish. Returns the value, so a caller can chain on it.
 */
export function rollRandom(ast, cueElement = null) {
  const { uid, min, max, asInt } = readParams(ast);

  // Rounding AFTER scaling, with max+1 as the exclusive bound, so every whole
  // number in min..max is equally likely. Math.round on a min..max float makes
  // the two endpoints half as likely as the rest, which shows up immediately
  // as a folder whose first and last picture come up rarely.
  const raw = Math.random();
  const value = asInt
    ? Math.min(max, min + Math.floor(raw * (max - min + 1)))
    : min + raw * (max - min);

  const span = max - min;
  publish("random", uid, {
    value,
    norm: span === 0 ? 0 : (value - min) / span,
  }, { source: "random" });

  if (cueElement) cueElement.dataset.oscillaUid = cueElement.dataset.oscillaUid || uid;
  return value;
}

export function handleRandomCue(ast, cueElement = null) {
  rollRandom(ast, cueElement);
  return true;
}
